import { defineStore } from 'pinia';
import IodineGrouperService, { InitData } from 'src/services/IodineGrouperService';
import { GrouperService } from 'src/services/GrouperService';
import { computed, ref } from 'vue';
import { PresentOnAdmission } from 'src/contracts/presentOnAdmission';
import { DiagnosisCode } from 'src/contracts/diagnosisCode';
import { ProcedureCode } from 'src/contracts/procedureCode';
import { PatientData } from 'src/contracts/patientData';
import { GrouperData } from 'src/contracts/grouperData';
import MockGetGrouperDataResponse from 'src/mock-data/MockGetGrouperDataResponse.json';
import MockProcedureCodes from 'src/mock-data/MockProcedureCodes.json';
import { addDiagnosisCodes } from 'src/common/codeUtil';
import { GrouperServiceConverter } from 'src/services/converters/GrouperServiceConverter';
import { Subscription } from 'rxjs';
import { Services } from 'src/services/service-manager';
import { DischargeStatus } from 'src/contracts/discharge-status';
import { isEmpty } from 'src/common/arrayUtil';
import { useLayoutStore } from 'stores/layout-store';


export const useGrouperStore = defineStore('grouperstore', () => {

  const diagnosisCodes = ref<DiagnosisCode[] | undefined>(GrouperServiceConverter.convertGrouperData(MockGetGrouperDataResponse)?.diagnosisCodes);
  const procedureCodes = ref<ProcedureCode[] | undefined>(MockProcedureCodes);
  const poas = ref<PresentOnAdmission[]>(Object.values(PresentOnAdmission));
  const dischargeStatuses = ref<DischargeStatus[]>([]);
  const getDischargeStatuses = computed(() => dischargeStatuses.value);
  const dischargeStatus = ref<DischargeStatus | undefined>();

  const patient = ref<PatientData | undefined>();
  const getPatient = computed(() => patient.value );

  function requestClose() {

    IodineGrouperService.close();
  }

  function resetDRGData() {
    diagnosisCodes.value = undefined;
    procedureCodes.value = undefined;
    dischargeStatus.value = undefined;
  }

  // temporary helper function
  function searchCodes(text: string | undefined): string {
    if (text === 'A.111') {
      return 'Diagnosis Code A.111';
    } else if (text === 'B.22') {
      return 'Diagnosis Code B.22';
    } else if (text === 'C.3') {
      return 'Diagnosis Code C.3';
    } else {
      return '';
    }
  }

  function addDiagnosisCode(code: string | undefined, description: string | undefined, poa: PresentOnAdmission | undefined): void {
    diagnosisCodes.value?.push({
      pdx: [],
      drgModifier: [],
      poa: poa || undefined,
      quality: [],
      risk: [],
      id: diagnosisCodes.value?.length || 0,
      action: '',
      code,
      description
    })
  }

  function addProcedureCode(code: string | undefined, description: string | undefined): void {
    procedureCodes.value?.push({
      action: '',
      code,
      id: diagnosisCodes.value?.length || 0,
      impact: [],
      slot: [],
      description
    })
  }

  let setVisitContextSubscription: Subscription | undefined;

  const addCodeSubscription = IodineGrouperService.addCodes$.subscribe(
    async (addCodeData: GrouperData) => {
        console.log(`Adding code(s): ${JSON.stringify(addCodeData, null, 4)}`);

        addDiagnosisCodes(diagnosisCodes.value, addCodeData.diagnosisCodes);
    }
  );

  function createContextSubscription() {
    setVisitContextSubscription = IodineGrouperService.setVisitContext$.subscribe(
      async (initData: InitData) => {
        console.log('Resetting DRG data');
        resetDRGData();
        useLayoutStore().changeMode(initData.mode, false);
        Services.setDefaultConfig(initData.endpoint, initData.visitId, initData.facilityCode);

        const grouperService = Services.service(GrouperService);
        const response = await grouperService.getGrouperData();

        if (isEmpty(dischargeStatuses.value)) {
          dischargeStatuses.value = await grouperService.getDischargeStatuses();
        }

        diagnosisCodes.value = response.diagnosisCodes;
        procedureCodes.value = response.procedureCodes;
        patient.value = response.patient;
      }
    );
  }

  function $onMount() {
    createContextSubscription();
  }

  function $reset() {
    setVisitContextSubscription && setVisitContextSubscription.unsubscribe();
    addCodeSubscription.unsubscribe();
  }

  return {
    diagnosisCodes,
    procedureCodes,
    poas,
    searchCodes,
    addDiagnosisCode,
    addProcedureCode,
    patient,
    getPatient,
    requestClose,
    $reset,
    $onMount,
    setVisitContextSubscription,
    addCodeSubscription,
    getDischargeStatuses,
    dischargeStatus
  }
})
